import React, { MouseEvent } from 'react';
import styled from 'styled-components';
// --------------------------------------------------------------------------------
import { String, StringProps } from './String';
import { Button, ButtonProps } from './Button';
import { config } from '../index';

export interface CardProps {
  /* Title props */
  title?: StringProps[];
  /* Body props */
  body?: StringProps[];
  /* Node props */
  node?: NodeProps[];
  /* Button props */
  button?: ButtonProps[];
  /* Title config props */
  titleConfig?: NodeConfigProps;
  /* Body config props */
  bodyConfig?: NodeConfigProps;
  /* Node config props */
  nodeConfig?: NodeConfigProps;
  /* Button config props */
  buttonConfig?: NodeConfigProps;
  /* Children config props */
  childrenConfig?: NodeConfigProps;
  /* Card border */
  border?: string;
  /* Card border bottom */
  borderBottom?: string;
  /* Card border top */
  borderTop?: string;
  /* Card border left */
  borderLeft?: string;
  /* Card border right */
  borderRight?: string;
  /* Card border radius */
  borderRadius?: string;
  /* Card background color */
  backgroundColor?: string;
  /* Card padding */
  padding?: string;
  /* Card margin */
  margin?: string;
  /* Card box shadow */
  boxShadow?: string;
  /* Card height */
  height?: string;
  /* Card max width */
  maxWidth?: string;
  /* Card min width */
  minWidth?: string;
  /* Card additional class props */
  className?: string;
  /* Children props */
  children?: React.ReactNode;
  /* onClick callBack */
  onClick?: (event?: MouseEvent) => void;
}

export interface NodeProps {
  node?: React.ReactNode;
  width?: string;
}

export interface NodeConfigProps {
  gap?: string;
  order?: number;
  width?: string;
  maxWidth?: string;
  margin?: string;
  backgroundColor?: string;
  padding?: string;
  border?: string;
  borderRadius?: string;
}

const StyledCardContainer = styled.div<CardProps>`
  display: flex;
  flex-direction: column;

  border: ${(props) => props.border};
  border-bottom: ${(props) => props.borderBottom};
  border-top: ${(props) => props.borderTop};
  border-left: ${(props) => props.borderLeft};
  border-right: ${(props) => props.borderRight};
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => props.borderRadius};
  padding: ${(props) => props.padding};
  margin: ${(props) => props.margin};
  box-shadow: ${(props) => props.boxShadow};
  height: ${(props) => props.height};
  max-width: ${(props) => props.maxWidth};
  min-width: ${(props) => props.minWidth};
`;

const StyledWrapper = styled.div<NodeConfigProps>`
  display: flex;
  flex-wrap: wrap;
  width: 100%;

  order: ${(props) => props.order};
  gap: ${(props) => props.gap};
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};
  width: ${(props) => props.width};
  background-color: ${(props) => props.backgroundColor};
  border: ${(props) => props.border};
  border-radius: ${(props) => props.borderRadius};
  max-width: ${(props) => props.maxWidth};
`;

const StyledChildren = styled.div<NodeConfigProps>`
  order: ${(props) => props.order};
  gap: ${(props) => props.gap};
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};
  width: ${(props) => props.width};
  background-color: ${(props) => props.backgroundColor};
  border: ${(props) => props.border};
  border-radius: ${(props) => props.borderRadius};
`;

const StyledButton = styled.div<NodeConfigProps>`
  display: flex;
  flex-wrap: wrap;

  order: ${(props) => props.order};
  gap: ${(props) => props.gap};
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};
  width: ${(props) => props.width};
  background-color: ${(props) => props.backgroundColor};
  border: ${(props) => props.border};
  border-radius: ${(props) => props.borderRadius};
`;

const StyledNode = styled.div<NodeConfigProps>`
  display: flex;
  flex-wrap: wrap;
  width: ${(props) => props.width};
  gap: ${(props) => props.gap};
`;

export const Card: React.FC<CardProps> = ({
  title,
  body,
  node,
  button,
  // --------------------------------------------------------------------------------
  titleConfig,
  bodyConfig,
  nodeConfig,
  buttonConfig,
  childrenConfig,
  // --------------------------------------------------------------------------------
  border = `1px solid ${config.muted}`,
  borderBottom,
  borderTop,
  borderLeft,
  borderRight,
  borderRadius = config.spacingSmall,
  backgroundColor = config.transparent,
  boxShadow = 'none',
  padding = config.spacingXMedium,
  margin,
  height,
  maxWidth,
  minWidth,
  children, // allows for children to be passed in & extends the Card component
  // additional props
  ...props
}) => {
  // 📌 don't return component if no properties values are specified
  if (!title && !body && !node && !button && !children) return null;

  return (
    <StyledCardContainer
      border={border}
      borderBottom={borderBottom}
      borderTop={borderTop}
      borderLeft={borderLeft}
      borderRight={borderRight}
      backgroundColor={backgroundColor}
      borderRadius={borderRadius}
      boxShadow={boxShadow}
      padding={padding}
      margin={margin}
      height={height}
      maxWidth={maxWidth}
      minWidth={minWidth}
      // any additional props that override the defaults
      {...props}
    >
      {!!title?.length && (
        <StyledWrapper
          order={titleConfig?.order}
          gap={titleConfig?.gap}
          width={titleConfig?.width}
          margin={titleConfig?.margin}
          padding={titleConfig?.padding}
          backgroundColor={titleConfig?.backgroundColor}
          border={titleConfig?.border}
          borderRadius={titleConfig?.borderRadius}
        >
          {title.map((props, key) => {
            return (
              <String
                key={key}
                // default title props
                fontSize={config.fontLarge}
                fontWeight="bold"
                align="center"
                width="100%"
                {...props}
              />
            );
          })}
        </StyledWrapper>
      )}

      {!!body?.length && (
        <StyledWrapper
          order={bodyConfig?.order}
          gap={bodyConfig?.gap}
          width={bodyConfig?.width}
          maxWidth={bodyConfig?.maxWidth}
          margin={bodyConfig?.margin}
          padding={bodyConfig?.padding}
          backgroundColor={bodyConfig?.backgroundColor}
          border={bodyConfig?.border}
          borderRadius={bodyConfig?.borderRadius}
        >
          {body.map((props, key) => {
            return (
              <String
                key={key}
                // default body props
                margin={`${config.spacingLarge} 0 0 0`}
                width="100%"
                {...props}
              />
            );
          })}
        </StyledWrapper>
      )}

      {!!node?.length && (
        <StyledWrapper
          order={nodeConfig?.order}
          gap={nodeConfig?.gap}
          width={nodeConfig?.width}
          margin={nodeConfig?.margin}
          padding={nodeConfig?.padding}
          backgroundColor={nodeConfig?.backgroundColor}
          border={nodeConfig?.border}
          borderRadius={nodeConfig?.borderRadius}
        >
          {node.map(({ node, width = 'inherit' }, key) => {
            return (
              <StyledNode key={key} width={width}>
                {node}
              </StyledNode>
            );
          })}
        </StyledWrapper>
      )}

      {!!button?.length && (
        <StyledButton
          order={buttonConfig?.order}
          gap={buttonConfig?.gap}
          width={buttonConfig?.width}
          margin={buttonConfig?.margin}
          padding={buttonConfig?.padding}
          backgroundColor={buttonConfig?.backgroundColor}
          border={buttonConfig?.border}
          borderRadius={buttonConfig?.borderRadius}
        >
          {button.map((props, key) => {
            return <Button key={key} {...props} />;
          })}
        </StyledButton>
      )}

      {children && (
        <StyledChildren
          order={childrenConfig?.order}
          gap={childrenConfig?.gap}
          width={childrenConfig?.width}
          margin={childrenConfig?.margin}
          padding={childrenConfig?.padding}
          backgroundColor={childrenConfig?.backgroundColor}
          border={childrenConfig?.border}
          borderRadius={childrenConfig?.borderRadius}
        >
          {children}
        </StyledChildren>
      )}
    </StyledCardContainer>
  );
};
